import React from 'react';

/**
 * PrintSheetTakeOff Component
 * 
 * This component handles the printing of a takeoff sheet by creating an iframe
 * with the necessary styling and content, then triggering the print dialog.
 * 
 * @param {Object} props
 * @param {string} props.containerId - The ID of the element containing the content to print
 * @param {Function} props.onError - Optional callback function to handle errors
 */
const PrintSheetTakeOff = ({ containerId = 'sheet-takeoff-container', onError }) => {
  const printSheetTakeOff = () => {
    const printContent = document.getElementById(containerId);
    
    if (printContent) {
      // Create an iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
      
      // Get the iframe document and write the print content
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      
      // Write the HTML content to the iframe with complete styling
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Takeoff Sheet</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css">
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Times+New+Roman:wght@400;700&display=swap');
              
              @media print {
                @page {
                  size: letter portrait;
                  margin: 0.5cm;
                }
                
                body {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                font-family: 'Times New Roman', Times, serif;
              }
              
              body {
                background-color: white;
                color: black;
                padding: 0;
                margin: 0;
              }
              
              .print-container {
                width: 100%;
                padding: 10px;
                margin: 0 auto;
              }
              
              /* ==== PRINTABLE SHEET ==== */
              .wrapper-sheet { 
                font-family: 'Times New Roman', Times, serif !important;
                color: black;
                border-color: black;
                transform: scale(1) !important;
                transform-origin: top center;
                width: 100% !important;
                height: auto !important;
              }
              
              .wrapper-sheet #header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                height: 80px;
                font-size: 15px;
              }
              
              .wrapper-sheet #header img {
                height: 100%;
              }
              
              .wrapper-sheet #header p {
                text-align: right;
                font-size: 13px;
              }
              
              .wrapper-sheet .content table {
                width: 100%;
                margin-top: 10px;
                border-collapse: collapse;
                border: 1px solid black;
              }
              
              .wrapper-sheet .content table th {
                background-color: black !important;
                color: white !important;
                height: 8px !important;
              }
              
              .wrapper-sheet .content table,
              .wrapper-sheet .content tr,
              .wrapper-sheet .content td,
              .wrapper-sheet .content th {
                border: 1px solid black !important;
                border-collapse: collapse;
                height: 8px !important;
              }
              
              .wrapper-sheet .content td {
                border: 1px solid black !important;
                padding-top: 1px !important;
                padding-bottom: 1px !important;
              }
              
              .content .description {
                text-align: left !important;
                padding-left: 5px !important;
                width: 265px !important;
                font-size: 13px !important;
              }

              .content .quantity {
                text-align: center !important;
                font-size: 13px !important;
              }
            
              .contact-grid {
                font-size: 15px;
                gap: 5px;
              }
              .contact-information {
                text-align: center !important;
                font-size: 13px;
              }
              
              /* Fix Bootstrap icons */
              .bi {
                display: inline-block !important;
                font-size: inherit;
                font-style: normal;
                font-weight: normal !important;
              }
              
              .bi-square:before {
                content: "\\F270";
                color: black !important;
              }
              
              /* Tailwind classes */
              .bg-white { background-color: white !important; }
              .p-\\[40px\\] { padding: 40px !important; }
              .mx-auto { margin-left: auto !important; margin-right: auto !important; }
              .my-\\[20px\\] { margin-top: 20px !important; margin-bottom: 20px !important; }
              .flex { display: flex !important; }
              .flex-col { flex-direction: column !important; }
              .w-full { width: 100% !important; }
              .grid { display: grid !important; }
              .items-center { align-items: center !important; }
              .gap-\\[50px\\] { gap: 50px !important; }
              .gap-\\[20px\\] { gap: 20px !important; }
              .gap-0 { gap: 0 !important; }
              .grid-cols-\\[2fr_1fr\\] { grid-template-columns: 2fr 1fr !important; }
              .grid-cols-\\[auto_3fr\\] { grid-template-columns: auto 3fr !important; }
              .grid-cols-\\[auto_2fr\\] { grid-template-columns: auto 2fr !important; }
              .grid-cols-\\[1fr_1fr_1fr\\] { grid-template-columns: 1fr 1fr 1fr !important; }
              .text-left { text-align: left !important; }
              .w-fit { width: fit-content !important; }
              .border-b { border-bottom: 1px solid black !important; }
              .border-black { border-color: black !important; }
              .p-0 { padding: 0 !important; }
              .m-0 { margin: 0 !important; }
            </style>
          </head>
          <body>
            <div class="print-container">
              ${printContent.innerHTML}
            </div>
            <script>
              // Print automatically when loaded
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 700);
              };
            </script>
          </body>
        </html>
      `);
      
      iframeDoc.close();
      
      // Remove the iframe after printing
      iframe.onload = () => {
        // The onafterprint event handles cleanup after printing
        iframe.contentWindow.onafterprint = () => {
          document.body.removeChild(iframe);
        };
      };
    } else {
      const errorMessage = 'Print content not found';
      console.error(errorMessage);
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  return (
    <button 
      onClick={printSheetTakeOff}
      className="text-gray-600 hover:text-primary py-5 w-full border border-gray-600 hover:border-primary rounded"
    > 
      <i className="text-[40px] bi bi-printer"></i> 
      <p> Print </p>
    </button>
  );
};

export default PrintSheetTakeOff; 